import React, { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../UserContext";
import Header from "../Header";
import apiClient from "../../api/client";
import SearchableSelect from "../SearchableSelect";
import { toast } from "react-toastify";
import { HiOutlineRefresh } from "react-icons/hi";
import { useNavigate, useSearchParams } from "react-router-dom";
import ExpiryFilterChip from "../ExpiryOverview/ExpiryFilterChip";
import ExpiryDate from "../ExpiryDate";
import PhoneCell from "../PhoneCell";
import LoadError from "../LoadError";
import DvplSyncCustomerModal from "./DvplSyncCustomerModal";
import { getErrorMessage } from "../../utils/network";
import { getExpiryBucket, normalizeExpiry } from "../../utils/expiry";
import Pagination from "../Pagination";
import "./index.css";

// type: "internet" (default) -> lists Internet customers
//       "dvpl"                -> lists DVPL customers
// Same list page, same columns/filters — just points internet_customer_list.php
// and the customer deep-link at the other provider.
const InternetCustomerList = ({ type = "internet" }) => {

    const { token, user } = useContext(UserContext);
    const role = user?.role;

    const providerLabel = type === "dvpl" ? "DVPL" : "Pioneer";
    const deepLinkParam = type === "dvpl" ? "dvpl" : "internet";

    const [customers, setCustomers] = useState([]);

    const [loading, setLoading] = useState(true);
    // { message, cause } when the list couldn't be loaded — shown instead of
    // the table, so a dropped connection no longer reads "No Customers Found".
    const [loadError, setLoadError] = useState(null);

    const [search, setSearch] = useState("");

    // Status filter is driven by the stat cards themselves — clicking
    // "Active" both shows the count AND filters to it, rather than
    // having a separate count display and a separate filter control
    // that could drift out of sync with each other.
    const [statusFilter, setStatusFilter] = useState("all");

    // Partner/branch filters — admin/superadmin only. For an operator,
    // the backend already restricts the list to their own single
    // branch, so a branch filter would only ever have one option and
    // isn't shown for that role.
    const [partnerFilter, setPartnerFilter] = useState("all");
    const [branchFilter, setBranchFilter] = useState("all");

    // DVPL page only: copy a customer over from the old RADIUS.
    const [showSync, setShowSync] = useState(false);


    const navigate = useNavigate();

    // ?expiry=today etc. — set by the dashboard's Expiry Overview buttons.
    const [searchParams, setSearchParams] = useSearchParams();
    const expiryBucket = getExpiryBucket(searchParams.get("expiry"));

    const clearExpiryFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("expiry");
        setSearchParams(next, { replace: true });
    };

    // Server-side pages: the API filters the provider's list and sends one page.
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(() => {
        try { return Number(sessionStorage.getItem("bb_customers_per_page")) || 50; } catch { return 50; }
    });
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, new: 0, online: 0, offline: 0, other: 0 });
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [query, setQuery] = useState("");
    const reqSeq = useRef(0);

    // debounce the search box
    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const loadCustomers = async (p = page, refresh = false) => {

        const my = ++reqSeq.current;
        setLoading(true);

        try {

            const { data } = await apiClient.get("/customer/internet_customer_list.php", {
                params: {
                    type,
                    page: p,
                    per_page: perPage,
                    q: query || undefined,
                    status: statusFilter,
                    partner: partnerFilter,
                    branch: branchFilter,
                    expiry: expiryBucket ? expiryBucket.key : undefined,
                    refresh: refresh ? 1 : undefined,
                },
            });

            if (my !== reqSeq.current) return;

            if (data.status === 200) {

                setCustomers(data.results || []);
                setTotal(Number(data.total || 0));
                if (data.stats) setStats(data.stats);
                setPartnerOptions(data.partners || []);
                setBranchOptions(data.branches || []);
                if (data.page && data.page !== p) setPage(data.page);
                setLoadError(null);

            } else {

                setLoadError({ message: data.message || "Unable to load customers", cause: null });

            }

        } catch (e) {

            if (my !== reqSeq.current) return;
            // Server errors (e.g. "integration disabled") come back as
            // HTTP 500 with a message — show that, not "Network Error".
            setLoadError({ message: getErrorMessage(e, "Unable to load customers"), cause: e });

        }

        if (my === reqSeq.current) setLoading(false);

    };

    // Any filter change: back to page 1
    useEffect(() => {
        setPage(1);
        loadCustomers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, query, statusFilter, partnerFilter, branchFilter, searchParams.get("expiry"), perPage]);

    const goPage = (p) => { setPage(p); loadCustomers(p); window.scrollTo({ top: 0, behavior: "smooth" }); };
    const changePerPage = (n) => {
        try { sessionStorage.setItem("bb_customers_per_page", String(n)); } catch { /* ignore */ }
        setPerPage(n);
    };
    const refreshList = () => loadCustomers(page, true);

    const toggleStatusFilter = (value) => {
        setStatusFilter((prev) => (prev === value ? "all" : value));
    };

    // Rows arrive filtered (and, for an expiry filter, sorted) from the server.
    const filteredCustomers = customers;


    // acc_ids currently being opened — a double click on a row must not fire
    // two lookups / two creations.
    const openingRef = useRef(new Set());

    // The provider has this customer but the portal does not (no `users`
    // row yet). Build the portal customer from the provider's record and
    // link the account — server side, see import_internet_customer.php —
    // instead of dead-ending on "Customer not found".
    const importProviderCustomer = async (customer) => {

        const { data } = await apiClient.post("/customer/import_internet_customer.php", {
            acc_id: customer.acc_id,
            type
        });

        if (!data.success) {
            throw new Error(data.message || "Unable to create the customer");
        }

        if (data.created_user) {
            toast.info(`${providerLabel} customer was not in the portal yet — created from their ${providerLabel} details.`);
        } else if (data.created_account) {
            toast.info(`${providerLabel} account linked to the existing portal customer.`);
        }

        return data.customer_id;

    };

    const redirectToCustomer = async (customer) => {

        if (openingRef.current.has(customer.acc_id)) return;
        openingRef.current.add(customer.acc_id);

        try {

            // Always go through the import: it returns the already linked account
            // straight away, links this provider account to a portal customer that
            // exists under the same mobile number, and only creates the customer
            // when there is none. Looking the customer up by mobile alone opened
            // customers that had no account linked, so nothing loaded.
            const customerId = await importProviderCustomer(customer);

            navigate(
                `/customer/${customerId}?${deepLinkParam}=${customer.acc_id}`
            );

        } catch (err) {

            toast.error(getErrorMessage(err, "Unable to open customer"));

        } finally {

            openingRef.current.delete(customer.acc_id);

        }

    };

    return (

        <>

            <Header />

            <div className="internet-list-container">

                <div className="internet-list-header">

                    <h2>{providerLabel} Customers</h2>

                    <div className="dvs-header-actions">
                        <button
                            className="refresh-btn"
                            onClick={refreshList}
                        >
                            Refresh
                        </button>
                        <button
                            className="refresh-btn-mobile"
                            onClick={refreshList}
                        >
                            <HiOutlineRefresh />
                        </button>

                        {type === "dvpl" && (
                            <button
                                type="button"
                                className="dvs-open-btn"
                                onClick={() => setShowSync(true)}
                            >
                                Sync Customer
                            </button>
                        )}
                    </div>


                </div>

                {/* Stat cards double as the status filter — click one to
                    filter to it, click again (or "Total") to clear. */}
                <div className="internet-stat-cards">
                    <button
                        className={`internet-stat-card ${statusFilter === "all" ? "active" : ""}`}
                        onClick={() => setStatusFilter("all")}
                    >
                        <span className="internet-stat-count">{stats.total}</span>
                        <span className="internet-stat-label">Total</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-active ${statusFilter === "active" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("active")}
                    >
                        <span className="internet-stat-count">{stats.active}</span>
                        <span className="internet-stat-label">Active</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-expired ${statusFilter === "expired" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("expired")}
                    >
                        <span className="internet-stat-count">{stats.expired}</span>
                        <span className="internet-stat-label">Expired</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-new ${statusFilter === "new" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("new")}
                    >
                        <span className="internet-stat-count">{stats.new}</span>
                        <span className="internet-stat-label">New</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-online ${statusFilter === "online" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("online")}
                    >
                        <span className="internet-stat-count">{stats.online}</span>
                        <span className="internet-stat-label">Online</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-offline ${statusFilter === "offline" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("offline")}
                    >
                        <span className="internet-stat-count">{stats.offline}</span>
                        <span className="internet-stat-label">Offline</span>
                    </button>
                    {stats.other > 0 && (
                        <div className="internet-stat-card stat-other">
                            <span className="internet-stat-count">{stats.other}</span>
                            <span className="internet-stat-label">Other</span>
                        </div>
                    )}
                </div>

                {expiryBucket && (
                    <ExpiryFilterChip
                        bucketKey={expiryBucket.key}
                        providerLabel={providerLabel}
                        count={loading ? null : total}
                        onClear={clearExpiryFilter}
                    />
                )}

                <div className="internet-list-filters">
                    <input
                        className="internet-search"
                        placeholder="Search Customer, Username, Mobile, Package..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    {/* Partner/branch filters — admin & superadmin only.
                        Operators are already restricted to a single branch
                        server-side, so this would only ever show one option
                        for them. */}
                    {(role === "admin" || role === "superadmin") && (
                        <>
                            <SearchableSelect
                                className="internet-filter-select"
                                options={partnerOptions}
                                value={partnerFilter}
                                onChange={setPartnerFilter}
                                allLabel="All Partners"
                            />

                            <SearchableSelect
                                className="internet-filter-select"
                                options={branchOptions}
                                value={branchFilter}
                                onChange={setBranchFilter}
                                allLabel="All Branches"
                            />
                        </>
                    )}
                </div>

                {loading ? (

                    <>
                        <div className="skeleton-loader">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="skeleton-row">
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                </div>
                            ))}
                        </div>
                    </>

                ) : loadError ? (

                    <LoadError error={loadError.cause} message={loadError.message} onRetry={() => loadCustomers(page)} />

                ) : (

                    <div className="operator-table-wrapper">

                        <table className="operator-table">

                            <thead>

                                <tr>

                                    <th>Username</th>
                                    <th>Status</th>
                                    <th>Mobile</th>
                                    <th>Name</th>
                                    <th>Expiry Date</th>
                                    <th>Package</th>
                                    <th>Sub Plan</th>

                                </tr>

                            </thead>

                            <tbody>

                                {filteredCustomers.length === 0 && (

                                    <tr>

                                        <td colSpan="7">

                                            No Customers Found

                                        </td>

                                    </tr>

                                )}

                                {filteredCustomers.map((c, index) => (

                                    <tr
                                        key={c.acc_id}
                                        // Previously set an unused selectedCustomer state, so
                                        // clicking a row did nothing. Now opens the customer
                                        // page on this provider's tab, like the username link.
                                        onClick={() => redirectToCustomer(c)}
                                        style={{ cursor: "pointer" }}
                                        title="Open customer details"
                                    >

                                        <td>
                                            <span
                                                className="internet-link"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    redirectToCustomer(c);
                                                }}
                                            >
                                                {c.username_org}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${c.status === "Active" ? c.online_status === "ONLINE" ? "active-green" : "active-gray" : "inactive"}`}>
                                                {c.status  === "Active" ? c.online_status === "ONLINE"
                                                    ? "ONLINE"
                                                    : "OFFLINE" : c.status}
                                            </span>
                                        </td>
                                        <td>
                                            {/* tel: opens the device's own dialer directly with the
                                                number pre-filled — no longer navigates to the
                                                customer profile, that's now the username's job. */}
                                            <PhoneCell number={c.mobile} />
                                        </td>
                                        
                                        <td>

                                            {c.firstname} {c.lastname}

                                        </td>
                                        <td>
                                            {normalizeExpiry(c.expiry_date) ? <ExpiryDate value={c.expiry_date} /> : (c.expiry_date || "—")}
                                        </td>
                                        <td>{c.package_name}</td>
                                        <td>{c.sub_plan_name}</td>
                                    </tr>

                                ))}

                            </tbody>

                        </table>

                        {total > 0 && (
                            <Pagination page={page} perPage={perPage} total={total} loading={loading}
                                onPage={goPage} onPerPage={changePerPage} />
                        )}

                    </div>

                )}

            </div>

            {type === "dvpl" && (
                <DvplSyncCustomerModal
                    visible={showSync}
                    onClose={() => setShowSync(false)}
                    role={role}
                    onSynced={refreshList}
                />
            )}

        </>
    );

};

export default InternetCustomerList;